if (!customElements.get('cus-announcement-countdown')) {
  customElements.define(
    'cus-announcement-countdown',
    class CusAnnouncementCountdown extends HTMLElement {
      connectedCallback() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;
        this.dayLabel = this.dataset.dayLabel || 'days';
        this.loopDays = parseInt(this.dataset.loopDays || '0', 10);
        this.targetTime = this.resolveTargetTime();
        this.render();
        if (this.targetTime) {
          this.intervalId = setInterval(() => this.render(), 1000);
        }
      }

      disconnectedCallback() {
        if (this.intervalId) clearInterval(this.intervalId);
      }

      parseDate(value) {
        if (!value) return null;
        const raw = String(value).trim();
        const match = raw.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
        if (match) {
          const year = Number(match[1]);
          const month = Number(match[2]) - 1;
          const day = Number(match[3]);
          const hour = Number(match[4] || 0);
          const minute = Number(match[5] || 0);
          const second = Number(match[6] || 0);
          const date = new Date(year, month, day, hour, minute, second);
          return Number.isNaN(date.getTime()) ? null : date;
        }
        const fallback = new Date(raw.replace(/-/g, '/'));
        return Number.isNaN(fallback.getTime()) ? null : fallback;
      }

      resolveTargetTime() {
        const initial = this.parseDate(this.dataset.date);
        if (!initial) return null;

        let target = initial.getTime();
        if (this.loopDays > 0) {
          const interval = this.loopDays * this.MS_IN_DAY;
          const now = Date.now();
          if (target <= now) {
            const cycles = Math.floor((now - target) / interval) + 1;
            target += cycles * interval;
          }
        }

        return target;
      }

      pad(value) {
        return value < 10 ? `0${value}` : String(value);
      }

      render() {
        if (!this.targetTime) {
          this.textContent = `00 ${this.dayLabel} 00:00:00`;
          return;
        }

        let remaining = this.targetTime - Date.now();
        if (remaining <= 0 && this.loopDays > 0) {
          this.targetTime = this.resolveTargetTime();
          remaining = this.targetTime - Date.now();
        }

        if (remaining <= 0) {
          remaining = 0;
          if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
          }
        }

        const days = Math.floor(remaining / this.MS_IN_DAY);
        const hours = Math.floor((remaining % this.MS_IN_DAY) / this.MS_IN_HRS);
        const minutes = Math.floor((remaining % this.MS_IN_HRS) / this.MS_IN_MIN);
        const seconds = Math.floor((remaining % this.MS_IN_MIN) / this.MS_IN_SEC);
        this.textContent = `${days} ${this.dayLabel} ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
      }
    }
  );
}

if (!customElements.get('cus-announcement-bar')) {
  customElements.define(
    'cus-announcement-bar',
    class CusAnnouncementBar extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.cus-announcement-bar');
        this.wrapper = this.querySelector('.swiper-wrapper');
        this.initClose();
        this.initSlider();
      }

      get slides() {
        return this.wrapper ? Array.from(this.wrapper.children) : [];
      }

      get autoplaySpeed() {
        const speed = parseFloat(this.getAttribute('autoplay-speed') || '0');
        return speed > 0 ? speed * 1000 : 0;
      }

      updateHeaderGroupHeight() {
        const header = document.querySelector('.header-section header');
        if (header && typeof header.setHeight === 'function') {
          header.setHeight();
        } else {
          window.dispatchEvent(new Event('resize'));
        }
      }

      hideBar() {
        if (!this.root) return;
        this.root.classList.add('is-hidden');
        this.root.setAttribute('aria-hidden', 'true');
        const section = this.root.closest('.shopify-section');
        if (section) section.style.display = 'none';
        this.updateHeaderGroupHeight();
      }

      initClose() {
        if (!this.root) return;

        const closeButton = this.root.querySelector('[data-cus-announcement-bar-close]');
        const storageKey = this.root.dataset.storageKey;
        const expiryDays = parseInt(this.root.dataset.expiryDays || '0', 10);
        const designMode = window.Shopify && window.Shopify.designMode;

        const persistHide = () => {
          if (window.FoxTheme && FoxTheme.utils && storageKey) {
            FoxTheme.utils.setStorage(storageKey, true, expiryDays || null);
          } else if (storageKey) {
            try {
              const item = { value: true };
              if (expiryDays > 0) item.expiry = Date.now() + expiryDays * 86400000;
              window.localStorage.setItem(storageKey, JSON.stringify(item));
            } catch (error) {
              // Ignore storage failures (private mode, iframe restrictions).
            }
          }
          this.hideBar();
        };

        if (closeButton) {
          closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            persistHide();
          });
        }

        if (designMode || !storageKey) return;

        try {
          let hidden = false;
          if (window.FoxTheme && FoxTheme.utils) {
            hidden = Boolean(FoxTheme.utils.getStorage(storageKey));
          } else {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
              const item = JSON.parse(raw);
              if (item.expiry && Date.now() > item.expiry) {
                window.localStorage.removeItem(storageKey);
              } else {
                hidden = Boolean(item.value);
              }
            }
          }
          if (hidden) this.hideBar();
        } catch (error) {
          // Ignore storage read failures.
        }
      }

      initSlider() {
        if (this.slides.length <= 1 || typeof FoxTheme === 'undefined' || !FoxTheme.Carousel) return;

        this.slides.forEach((slide, index) => {
          slide.style.display = index === 0 ? '' : 'none';
        });
        const sliderHeight = Math.ceil(
          Math.max(this.slides[0]?.getBoundingClientRect().height || 0, this.clientHeight || 0, 36)
        );
        this.slides.forEach((slide) => {
          slide.style.display = '';
        });
        this.style.height = `${sliderHeight}px`;
        this.classList.add('swiper-vertical');

        const modules = [FoxTheme.Swiper.Autoplay];
        const options = {
          direction: 'vertical',
          slidesPerView: 1,
          height: sliderHeight,
          loop: true,
          autoHeight: false,
          allowTouchMove: true,
          grabCursor: false,
          speed: 450,
          preventClicks: false,
          preventClicksPropagation: false,
          navigation: false,
          pagination: false,
          autoplay: this.autoplaySpeed
            ? { delay: this.autoplaySpeed, pauseOnMouseEnter: true, disableOnInteraction: false }
            : false,
        };

        this.carousel = new FoxTheme.Carousel(this, options, modules);
        this.carousel.init();

        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const index = this.slides.indexOf(event.target);
            if (index >= 0 && this.carousel && this.carousel.slider) {
              this.carousel.slider.slideToLoop(index);
            }
          });
        }
      }
    }
  );
}
